import React, { useState } from "react";
import "../Styles/CustomerStory.scss";
import { ArrowRight, Sparkles, X, ExternalLink, Building2, CheckCircle2 } from "lucide-react";
import { Modal } from "antd";

// Exact SVG double quote icon matching design system
const CustomQuoteIcon = ({ size = 18, color = "#F05742" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M7 8a2.5 2.5 0 1 0 0 5h1a1 1 0 0 0 1-1V9.5a1.5 1.5 0 0 0-1.5-1.5H7zm0 5c0 2.8-1.5 4.5-3.5 5.5" />
    <path d="M17 8a2.5 2.5 0 1 0 0 5h1a1 1 0 0 0 1-1V9.5a1.5 1.5 0 0 0-1.5-1.5H17zm0 5c0 2.8-1.5 4.5-3.5 5.5" />
  </svg>
);

const CustomerStory = ({ content = {} }) => {
  const { stories = [] } = content;
  const [activeStory, setActiveStory] = useState(null);

  const handleOpenStory = (story) => {
    setActiveStory(story);
  };

  const handleCloseModal = () => {
    setActiveStory(null);
  };

  return (
    <div className="customerStory-wrapper">
      <div className="customerStory-container">
        {/* Apple Editorial Header Meta */}
        <div className="customerStory-header-meta">
          <span className="customerStory-label-apple">CUSTOMER STORIES</span>
          <h2 className="customerStory-main-title">Proven Enterprise Outcomes</h2>
          <p className="customerStory-main-sub">
            Explore how leading brands and institutions achieve measurable business transformation with iNextLabs.
          </p>
        </div>

        {/* Apple Spatial Ceramic Cards 3-Column Grid */}
        <div className="customerStory-card-wrapper">
          {stories.map((story, index) => (
            <div
              key={index}
              className="customerStory-card ios-touch-item"
              onClick={() => handleOpenStory(story)}
            >
              {/* Card Image Banner */}
              {story.image && (
                <div className="customerStory-image-container">
                  <img
                    src={story.image}
                    alt={story.title || story.client}
                    className="customerStory-card-img"
                    loading="lazy"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                  <div className="customerStory-img-overlay" />
                  {story.stat && (
                    <span className="customerStory-floating-stat-pill">
                      <Sparkles size={12} className="stat-sparkle-icon" />
                      {story.stat}
                    </span>
                  )}
                </div>
              )}

              <div className="customerStory-card-body">
                {/* Header Row: Quote Icon + Industry Tag */}
                <div className="customerStory-top-row">
                  <div className="customerStory-quote-badge">
                    <CustomQuoteIcon size={18} color="#F05742" />
                  </div>
                  {story.tag && (
                    <span className="customerStory-industry-tag">{story.tag}</span>
                  )}
                </div>

                {/* Story Title / Headline */}
                {story.title && (
                  <h4 className="customerStory-card-headline">{story.title}</h4>
                )}

                {/* Quote & Description Content */}
                <div className="customerStory-content">
                  <p className="customerStory-description">"{story.text}"</p>
                </div>

                {/* Apple Link CTA Footer */}
                <div className="customerStory-card-footer">
                  <div className="customerStory-link-btn">
                    <span>Explore Case Details</span>
                    <ArrowRight size={15} className="customerStory-btn-arrow" />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* In-App Apple Spatial Case Study Modal */}
      {activeStory && (
        <Modal
          open={Boolean(activeStory)}
          onCancel={handleCloseModal}
          footer={null}
          centered={typeof window !== "undefined" && window.innerWidth >= 768}
          width={700}
          className="apple-spatial-casestudy-modal"
          closeIcon={
            <div className="apple-modal-close-pill" aria-label="Close">
              <X size={16} />
            </div>
          }
        >
          <div className="casestudy-modal-content" data-lenis-prevent="true">
            {/* iOS Native Sheet Grabber Bar (Mobile) */}
            <div className="ios-sheet-grabber">
              <div className="ios-sheet-grabber-pill" />
            </div>

            {/* Modal Top Hero Banner (Fixed at top, never scrolls) */}
            {activeStory.image && (
              <div className="casestudy-modal-hero-wrap">
                <img
                  src={activeStory.image}
                  alt={activeStory.title || activeStory.client}
                  className="casestudy-modal-hero-img"
                  loading="lazy"
                />
                <div className="casestudy-modal-hero-overlay" />
                {activeStory.stat && (
                  <span className="casestudy-modal-stat-pill">
                    <Sparkles size={13} />
                    {activeStory.stat}
                  </span>
                )}
              </div>
            )}

            {/* Scrollable Content Body (Only text & details scroll) */}
            <div className="casestudy-modal-scroll-body" data-lenis-prevent="true">
              <div className="casestudy-modal-inner">
                {/* Meta Tag & Client Row */}
                <div className="casestudy-modal-meta-row">
                  {activeStory.tag && (
                    <span className="casestudy-modal-tag">{activeStory.tag}</span>
                  )}
                  {activeStory.client && (
                    <span className="casestudy-modal-client">
                      <Building2 size={13} color="#F05742" />
                      {activeStory.client}
                    </span>
                  )}
                </div>

                {/* Title */}
                <h3 className="casestudy-modal-title">{activeStory.title}</h3>

                {/* Executive Summary Quote Box */}
                <div className="casestudy-modal-quote-box">
                  <div className="modal-quote-icon">
                    <CustomQuoteIcon size={20} color="#F05742" />
                  </div>
                  <p className="modal-quote-text">{activeStory.text}</p>
                </div>

                {/* Enterprise Transformation Highlights */}
                <div className="casestudy-modal-highlights">
                  <h5 className="modal-highlights-title">Enterprise Solution Impact</h5>
                  <div className="modal-highlights-list">
                    <div className="modal-highlight-item">
                      <CheckCircle2 size={16} color="#10b981" />
                      <span>24/7 intelligent automated resolution with human escalation workflows</span>
                    </div>
                    <div className="modal-highlight-item">
                      <CheckCircle2 size={16} color="#10b981" />
                      <span>Seamless integration into native enterprise CRMs, ERPs, and databases</span>
                    </div>
                    <div className="modal-highlight-item">
                      <CheckCircle2 size={16} color="#10b981" />
                      <span>Enterprise-grade data encryption and zero model training on customer data</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sticky Footer CTA & Actions */}
              <div className="casestudy-modal-footer">
                {activeStory.url && (
                  <a
                    href={activeStory.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="casestudy-modal-primary-btn"
                  >
                    <span>Read Full Story on inextlabs.ai</span>
                    <ExternalLink size={14} />
                  </a>
                )}
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="casestudy-modal-secondary-btn"
                >
                  Close Overview
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default CustomerStory;
